/**
 * AlphaZero Service — manages the Web Worker for browser-side neural network inference.
 *
 * Usage:
 *   const az = new AlphaZeroService();
 *   await az.init(rows, cols);
 *   const result = await az.selectAction(gameState, { simulations: 100 });
 *   // result = { actionType, position, value, policy, stats }
 *   az.terminate();
 */

export default class AlphaZeroService {
    constructor() {
        this.worker = null;
        this.ready = false;
        this._pendingResolve = null;
        this._pendingReject = null;
        this._onProgress = null;
    }

    /**
     * Initialize the worker and load the ONNX model.
     * @param {number} rows - Board rows
     * @param {number} cols - Board cols
     * @param {string} modelUrl - Optional override for model URL
     */
    async init(rows = 21, cols = 15, modelUrl = null) {
        if (this.worker) {
            this.worker.terminate();
        }

        this.ready = false;
        this.worker = new Worker('/alphaZeroWorker.js');

        // Determine model URL from manifest or default
        const url = modelUrl || await this._resolveModelUrl(rows, cols);

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('AlphaZero worker initialization timed out'));
            }, 60000);

            this.worker.onmessage = (e) => {
                const { type } = e.data;

                if (type === 'ready') {
                    clearTimeout(timeout);
                    this.ready = true;
                    this.worker.onmessage = this._handleMessage.bind(this);
                    resolve();
                } else if (type === 'error') {
                    clearTimeout(timeout);
                    reject(new Error(e.data.message));
                }
            };

            this.worker.onerror = (err) => {
                clearTimeout(timeout);
                reject(err);
            };

            this.worker.postMessage({ type: 'init', modelUrl: url, rows, cols });
        });
    }

    async _resolveModelUrl(rows, cols) {
        // Try to fetch manifest
        try {
            const resp = await fetch('/models/manifest.json');
            if (resp.ok) {
                const manifest = await resp.json();
                const key = `${rows}x${cols}`;
                if (manifest.models && manifest.models[key]) {
                    return `/models/${manifest.models[key].path}`;
                }
            }
        } catch (e) {
            // Fall through to default
        }
        return `/models/model_${rows}x${cols}.onnx`;
    }

    _handleMessage(e) {
        const { type } = e.data;

        if (type === 'action' || type === 'fullMove') {
            if (this._pendingResolve) {
                this._pendingResolve(e.data);
                this._pendingResolve = null;
                this._pendingReject = null;
            }
        } else if (type === 'progress') {
            if (this._onProgress) {
                this._onProgress(e.data.simsDone, e.data.simsTotal);
            }
        } else if (type === 'error') {
            if (this._pendingReject) {
                this._pendingReject(new Error(e.data.message));
                this._pendingResolve = null;
                this._pendingReject = null;
            }
        }
    }

    /**
     * Select an action using MCTS.
     * @param {Object} gameState - { board, phutballPos, currentPlayer, isJumping, jumpSeq, jumpVisitCounts, winner }
     * @param {Object} options - { simulations, temperature, onProgress }
     * @returns {Promise<{ actionType, position, value, policy, stats }>}
     */
    async selectAction(gameState, { simulations = 100, temperature = 0.0, onProgress = null } = {}) {
        if (!this.ready || !this.worker) {
            throw new Error('AlphaZero service not initialized');
        }

        this._onProgress = onProgress;

        return new Promise((resolve, reject) => {
            this._pendingResolve = resolve;
            this._pendingReject = reject;

            // Timeout for long searches
            const timeout = setTimeout(() => {
                reject(new Error('MCTS search timed out'));
                this._pendingResolve = null;
                this._pendingReject = null;
            }, 120000);

            const origResolve = resolve;
            this._pendingResolve = (data) => {
                clearTimeout(timeout);
                origResolve(data);
            };

            this.worker.postMessage({
                type: simulations > 0 ? 'selectAction' : 'evaluate',
                state: {
                    board: gameState.board,
                    phutballPos: gameState.phutballPos,
                    currentPlayer: gameState.currentPlayer,
                    isJumping: gameState.isJumping || false,
                    jumpSeq: gameState.jumpSeq || [],
                    jumpVisitCounts: gameState.jumpVisitCounts || {},
                    winner: gameState.winner || 0,
                },
                simulations,
                temperature,
            });
        });
    }

    /**
     * Quick policy-only evaluation (no MCTS, instant).
     */
    async quickEval(gameState) {
        return this.selectAction(gameState, { simulations: 0 });
    }

    /**
     * Select a complete move (placement, or full jump sequence).
     * Returns { move: { type, path }, value, stats }
     * The move format matches the server's commit_move expectation.
     */
    async selectFullMove(gameState, { simulations = 100, temperature = 0.0, onProgress = null } = {}) {
        if (!this.ready || !this.worker) {
            throw new Error('AlphaZero service not initialized');
        }

        this._onProgress = onProgress;

        return new Promise((resolve, reject) => {
            this._pendingResolve = resolve;
            this._pendingReject = reject;

            const timeout = setTimeout(() => {
                reject(new Error('MCTS search timed out'));
                this._pendingResolve = null;
                this._pendingReject = null;
            }, 300000); // 5 min for full move with multiple jump steps

            const origResolve = resolve;
            this._pendingResolve = (data) => {
                clearTimeout(timeout);
                origResolve(data);
            };

            this.worker.postMessage({
                type: 'selectFullMove',
                state: {
                    board: gameState.board,
                    phutballPos: gameState.phutballPos,
                    currentPlayer: gameState.currentPlayer,
                    isJumping: gameState.isJumping || false,
                    jumpSeq: gameState.jumpSeq || [],
                    jumpVisitCounts: gameState.jumpVisitCounts || {},
                    winner: gameState.winner || 0,
                },
                simulations,
                temperature,
            });
        });
    }

    terminate() {
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
            this.ready = false;
        }
    }

    get isReady() {
        return this.ready;
    }
}
